import React, { useMemo } from "react";
import { ClipboardList, Copy } from "lucide-react";
import { toast } from "sonner";
import { buildPoints, mapFittings, validSegs } from "@/lib/pipe3dPoints";

const TR = {
  sk: { tee: "T-kus", title: "Materiál na objednávku", pipe: "Rúra", elbow: "Koleno", flange: "Príruba (pár)", valve: "Ventil", reducer: "Redukcia", welds: "Zvary (odhad)", pcs: "ks", note: "Dĺžka os–os bez odpočtu kolien. Zvary: tupé spoje na kolenách, redukciách a prírubách. Vždy over podľa výkresu.", unknown: "{n} úsek(y) bez rozmeru", copy: "Kopírovať objednávku", copied: "Objednávka skopírovaná", order: "Objednávka materiálu" },
  en: { tee: "Tee", title: "Material for order", pipe: "Pipe", elbow: "Elbow", flange: "Flange (pair)", valve: "Valve", reducer: "Reducer", welds: "Welds (estimate)", pcs: "pcs", note: "Centreline length without elbow deduction. Welds: butt joints at elbows, reducers and flanges. Always verify against the drawing.", unknown: "{n} section(s) without dimension", copy: "Copy order", copied: "Order copied", order: "Material order" },
  cs: { tee: "T-kus", title: "Materiál k objednání", pipe: "Trubka", elbow: "Koleno", flange: "Příruba (pár)", valve: "Ventil", reducer: "Redukce", welds: "Svary (odhad)", pcs: "ks", note: "Délka osa–osa bez odpočtu kolen. Svary: tupé spoje na kolenech, redukcích a přírubách. Vždy ověř podle výkresu.", unknown: "{n} úsek(y) bez rozměru", copy: "Kopírovat objednávku", copied: "Objednávka zkopírována", order: "Objednávka materiálu" },
  de: { tee: "T-Stück", title: "Material zur Bestellung", pipe: "Rohr", elbow: "Bogen", flange: "Flansch (Paar)", valve: "Ventil", reducer: "Reduzierung", welds: "Schweißnähte (Schätzung)", pcs: "Stk", note: "Länge Achse–Achse ohne Bogenabzug. Nähte: Stumpfnähte an Bögen, Reduzierungen und Flanschen. Immer mit der Zeichnung prüfen.", unknown: "{n} Abschnitt(e) ohne Maß", copy: "Bestellung kopieren", copied: "Bestellung kopiert", order: "Materialbestellung" },
  pl: { tee: "Trójnik", title: "Materiał do zamówienia", pipe: "Rura", elbow: "Kolano", flange: "Kołnierz (para)", valve: "Zawór", reducer: "Redukcja", welds: "Spoiny (szacunek)", pcs: "szt.", note: "Długość oś–oś bez odliczenia kolan. Spoiny: doczołowe na kolanach, redukcjach i kołnierzach. Zawsze sprawdź z rysunkiem.", unknown: "{n} odcinek(ki) bez wymiaru", copy: "Kopiuj zamówienie", copied: "Zamówienie skopiowane", order: "Zamówienie materiału" },
  fr: { tee: "Té", title: "Matériel à commander", pipe: "Tube", elbow: "Coude", flange: "Bride (paire)", valve: "Vanne", reducer: "Réduction", welds: "Soudures (estimation)", pcs: "pcs", note: "Longueur axe–axe sans déduction des coudes. Soudures : bout à bout aux coudes, réductions et brides. Vérifie toujours avec le plan.", unknown: "{n} section(s) sans cote", copy: "Copier la commande", copied: "Commande copiée", order: "Commande de matériel" },
};

export function computeMaterial(segments, fittings) {
  const segs = validSegs(segments);
  const pts = buildPoints(segments) || [];
  const lengthMm = segs.reduce((a, s) => a + (Number(s.length_mm) > 0 ? Number(s.length_mm) : 0), 0);
  const unknown = segs.filter((s) => !(Number(s.length_mm) > 0)).length;
  const elbows = {};
  for (let i = 1; i < pts.length - 1; i++) {
    const d1 = pts[i].map((v, k) => v - pts[i - 1][k]);
    const d2 = pts[i + 1].map((v, k) => pts[i + 1][k] - pts[i][k]);
    const n = (d) => Math.hypot(...d);
    const cos = d1.reduce((a, v, k) => a + v * d2[k], 0) / (n(d1) * n(d2));
    const deg = Math.round((Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI);
    if (deg < 5) continue;
    const bucket = Math.abs(deg - 90) <= 10 ? 90 : Math.abs(deg - 45) <= 10 ? 45 : deg;
    elbows[bucket] = (elbows[bucket] || 0) + 1;
  }
  const fits = mapFittings(segments, fittings);
  const count = (t) => fits.filter((f) => f.type === t).length;
  const valves = count("valve");
  const reducers = count("reducer");
  const tees = count("tee");
  const flanges = count("flange") + valves * 2;
  const nElbows = Object.values(elbows).reduce((a, b) => a + b, 0);
  const welds = 2 * (nElbows + reducers + count("flange") + valves) + 3 * tees;
  return { lengthMm, unknown, elbows, flanges, valves, reducers, tees, welds };
}

export const MaterialList = ({ segments, fittings, diameter, lang }) => {
  const t = TR[lang] || TR.sk;
  const m = useMemo(() => computeMaterial(segments, fittings), [segments, fittings]);
  const dn = diameter ? ` ${diameter}` : "";
  const rows = [
    [`${t.pipe}${dn}`, `${(m.lengthMm / 1000).toFixed(2)} m`, "pipe"],
    ...Object.entries(m.elbows).sort((a, b) => b[0] - a[0]).map(([deg, n]) => [`${t.elbow} ${deg}°${dn}`, `${n} ${t.pcs}`, `elbow-${deg}`]),
    m.flanges ? [`${t.flange}${dn}`, `${m.flanges} ${t.pcs}`, "flange"] : null,
    m.valves ? [`${t.valve}${dn}`, `${m.valves} ${t.pcs}`, "valve"] : null,
    m.reducers ? [t.reducer, `${m.reducers} ${t.pcs}`, "reducer"] : null,
    m.tees ? [`${t.tee}${dn}`, `${m.tees} ${t.pcs}`, "tee"] : null,
    [t.welds, `${m.welds} ${t.pcs}`, "welds"],
  ].filter(Boolean);

  const copy = async () => {
    const text = [`${t.order}${dn}`, ...rows.map(([k, v]) => `- ${k}: ${v}`),
      m.unknown ? `(${t.unknown.replace("{n}", m.unknown)})` : null].filter(Boolean).join("\n");
    try { await navigator.clipboard.writeText(text); toast.success(t.copied); } catch { toast.error("Error"); }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-950 p-4 space-y-3" data-testid="pipeviz-material">
      <div className="font-mono uppercase tracking-widest text-[10px] text-amber-400 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {t.title}</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v, id]) => (
            <tr key={id} className="border-b border-zinc-900 last:border-0">
              <td className="py-1.5 text-zinc-300">{k}</td>
              <td className="py-1.5 text-right font-mono font-bold text-white" data-testid={`pipeviz-material-${id}`}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {m.unknown > 0 && <div className="text-xs text-amber-400">{t.unknown.replace("{n}", m.unknown)}</div>}
      <div className="text-xs text-zinc-500">{t.note}</div>
      <button onClick={copy} data-testid="pipeviz-material-copy" className="px-4 py-2.5 text-sm border border-zinc-700 text-white hover:border-amber-500 transition-colors inline-flex items-center gap-2">
        <Copy className="w-4 h-4" /> {t.copy}
      </button>
    </div>
  );
};
